import { useQueryClient } from '@tanstack/react-query';
import { FC, ReactNode, createContext, useCallback, useContext, useState } from 'react';

import { UpdateUserRequestBodyDTO } from 'declarations/dto/user';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { StorageE } from 'declarations/enums/storage';
import { UpdateUserI, UserProfileI } from 'declarations/interfaces/user';
import { useFetchUser, useUpdateUser } from 'hooks/api/useUser';
import { usePendo } from 'hooks/common/usePendo';

export type UserProfileContextT = {
  userProfile: UserProfileI | null;
  isLoadingUserProfile: boolean;
  isSuccessfullyFetchedUserProfile: boolean;
  isUpdatingProfile: boolean;
  isSuccessfullyUpdatedProfile: boolean;
  isColumnPickerPopupOpened: boolean;
  updateUser: (updateUser: UpdateUserI) => void;
  deleteUserPhoto: () => void;
  setColumnPickerPopupVisibility: (isOpened: boolean) => void;
};

type UserProfileProviderPropsT = { children: ReactNode };

const contextDefaultValue = {
  userProfile: null,
  isLoadingUserProfile: false,
  isSuccessfullyFetchedUserProfile: false,
  isUpdatingProfile: false,
  isSuccessfullyUpdatedProfile: false,
  updateUser: () => Promise.resolve(null),
  deleteUserPhoto: () => Promise.resolve(null),
  isColumnPickerPopupOpened: false,
  setColumnPickerPopupVisibility: () => undefined,
};

export const UserProfileContext = createContext<UserProfileContextT>(contextDefaultValue);

const UserProfileProvider: FC<UserProfileProviderPropsT> = ({ children }) => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const idToken = localStorage.getItem(StorageE.IdToken);
  const [isColumnPickerPopupOpened, setColumnPickerPopupVisibility] = useState<boolean>(false);

  /**
   * Api hooks
   */
  const getUserResult = useFetchUser(idToken);
  const updateUserMutation = useUpdateUser({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchUser]);
    },
  });

  /**
   * Initialize Pendo when user profile is loaded
   */
  usePendo(getUserResult.data || null, getUserResult.isSuccess);

  /**
   * Handlers
   */
  const updateUser = useCallback(
    (data: UpdateUserI) => {
      const file = data.photoFiles ? data.photoFiles[0] : null;
      const phoneNumber = data.phoneNumber?.replace(/[^a-zA-Z0-9]/g, '') ?? '';
      const bodyFormData = new FormData();

      bodyFormData.set('UserId', getUserResult.data?.id!);
      bodyFormData.set('Firstname', data.firstname!);
      bodyFormData.set('Lastname', data.lastname!);
      // Experience service requires null for optional values when not provided
      bodyFormData.set(
        'PhoneNumber',
        phoneNumber === '' ? (null as unknown as string) : phoneNumber
      );
      bodyFormData.set('Photo', file ?? (null as unknown as Blob));
      bodyFormData.set('IsActive', 'true');
      bodyFormData.set('DeletePhoto', 'false');
      bodyFormData.set('Email', getUserResult.data?.email!);

      updateUserMutation.mutate(bodyFormData as UpdateUserRequestBodyDTO);
    },
    [getUserResult.data?.id, updateUserMutation, getUserResult.data?.email]
  );

  const deleteUserPhoto = () => {
    const bodyFormData = new FormData();

    bodyFormData.set('UserId', getUserResult.data!.id!);
    bodyFormData.set('Firstname', getUserResult.data!.firstname!);
    bodyFormData.set('Lastname', getUserResult.data!.lastname!);
    // Experience service requires null for optional values when not provided
    const phoneNumber = getUserResult.data!.phoneNumber ?? '';
    bodyFormData.set('PhoneNumber', phoneNumber === '' ? (null as unknown as string) : phoneNumber);
    bodyFormData.set('Photo', null as unknown as Blob);
    bodyFormData.set('IsActive', 'true');
    bodyFormData.set('DeletePhoto', 'true');
    bodyFormData.set('Email', getUserResult.data!.email!);
    updateUserMutation.mutate(bodyFormData as UpdateUserRequestBodyDTO);
  };

  return (
    <UserProfileContext.Provider
      value={{
        userProfile: getUserResult.data || null,
        isLoadingUserProfile: getUserResult.isLoading || getUserResult.isRefetching,
        isSuccessfullyFetchedUserProfile: getUserResult.isSuccess,
        isUpdatingProfile: updateUserMutation.isLoading,
        isSuccessfullyUpdatedProfile: updateUserMutation.isSuccess,
        updateUser,
        deleteUserPhoto,
        isColumnPickerPopupOpened,
        setColumnPickerPopupVisibility,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};

const useUserProfile = () => useContext(UserProfileContext);

export { UserProfileProvider, useUserProfile };
